import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import StatusBadge from "@/components/gate-check/StatusBadge";
import { toast } from "sonner";
import { fmtIST } from "@/lib/datetime";
import { MagnifyingGlass, SignOut, CheckCircle, XCircle, IdentificationCard, PencilSimple, CaretUpDown, Check } from "@phosphor-icons/react";

const STATUSES = ["all", "pending", "approved", "checked_in", "checked_out", "rejected"];

// IST-day bounds as UTC ISO strings (check_in_at / check_out_at are stored UTC).
function istTodayRangeUTC() {
    const now = new Date();
    const istNow = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const startIST = new Date(istNow); startIST.setHours(0, 0, 0, 0);
    const endIST = new Date(startIST); endIST.setDate(endIST.getDate() + 1);
    const toUTC = (d) => new Date(d.getTime() - 5.5 * 3600 * 1000).toISOString();
    return [toUTC(startIST), toUTC(endIST)];
}

export default function VisitorLog() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const canCorrect = (user?.capabilities || []).includes("correct_visit_details")
        || ["admin", "admin_addl"].includes(user?.role);
    const [visits, setVisits] = useState([]);
    const [gates, setGates] = useState([]);
    const [q, setQ] = useState(searchParams.get("id") || "");
    const initialStatus = searchParams.get("status");
    const [statusFilter, setStatusFilter] = useState(
        initialStatus && STATUSES.includes(initialStatus) ? initialStatus : "all"
    );
    const [gateFilter, setGateFilter] = useState("all");
    const [todayMode, setTodayMode] = useState(searchParams.get("today") || ""); // "" | "in" | "out"
    const [loading, setLoading] = useState(false);
    const [editVisit, setEditVisit] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter !== "all") params.set("status", statusFilter);
            if (gateFilter !== "all") params.set("gate_id", gateFilter);
            if (q) params.set("q", q);
            if (todayMode === "in" || todayMode === "out") {
                const [from, to] = istTodayRangeUTC();
                if (todayMode === "in") {
                    params.set("date_from", from); params.set("date_to", to);
                } else {
                    params.set("out_from", from); params.set("out_to", to);
                    if (statusFilter === "all") params.set("status", "checked_out");
                }
            }
            const { data } = await api.get(`/gate-check/visits?${params.toString()}`);
            setVisits(data);
        } finally { setLoading(false); }
    };

    useEffect(() => { (async () => {
        const g = await api.get("/gate-check/gates"); setGates(g.data);
    })(); }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, gateFilter, todayMode]);

    const checkOut = async (id) => {
        try {
            await api.post(`/gate-check/visits/${id}/checkout`);
            toast.success("Checked out");
            load();
        } catch (e) { toast.error("Checkout failed"); }
    };

    const setStatus = async (id, status) => {
        try {
            await api.post(`/gate-check/visits/${id}/status`, { status });
            toast.success(status === "approved" ? "Approved" : "Rejected");
            load();
        } catch (e) { toast.error("Status update failed"); }
    };

    return (
        <div>
            <EditVisitDialog
                visit={editVisit}
                onClose={() => setEditVisit(null)}
                onSaved={() => { setEditVisit(null); load(); }}
            />
            <PageHeader
                overline="Visitor Records"
                title="Visitor Log"
                subtitle="All visits across gates. Filter by status, gate or search by name, phone, or company."
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" className="h-11" onClick={() => nav("/checkpoint/reports")}
                            data-testid="btn-goto-reports">
                            Export
                        </Button>
                        <Button className="h-11 bg-brand hover:bg-brand/90 text-white" onClick={() => nav("/checkpoint/checkin")}
                            data-testid="btn-new-checkin">
                            + New Check-in
                        </Button>
                    </div>
                }
            />
            <div className="p-6 space-y-4">
                <Card className="tech-card p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="md:col-span-2 relative">
                            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search name, phone, company…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                data-testid="input-log-search"
                                className="h-11 pl-9"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="h-11" data-testid="select-status-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUSES.map((s) => (
                                    <SelectItem key={s} value={s}>{s.replace("_", " ").toUpperCase()}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={gateFilter} onValueChange={setGateFilter}>
                            <SelectTrigger className="h-11" data-testid="select-gate-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">ALL GATES</SelectItem>
                                {gates.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </Card>

                {todayMode && (
                    <div className="flex items-center gap-2 text-[13px]" data-testid="today-filter-banner">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 text-brand px-3 py-1 font-semibold">
                            {todayMode === "in" ? "Checked in today (IST)" : "Checked out today (IST)"}
                        </span>
                        <button
                            className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
                            onClick={() => setTodayMode("")}
                            data-testid="btn-clear-today-filter"
                        >
                            Clear date filter
                        </button>
                    </div>
                )}

                <Card className="tech-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary">
                                <tr className="text-left">
                                    <Th>Visitor</Th>
                                    <Th>Phone</Th>
                                    <Th>Host</Th>
                                    <Th>Gate</Th>
                                    <Th>Visit Type</Th>
                                    <Th>Purpose of Visit</Th>
                                    <Th>Check-in</Th>
                                    <Th>Check-out</Th>
                                    <Th>Status</Th>
                                    <Th align="right">Actions</Th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {loading && (
                                    <tr><td colSpan={10} className="text-center p-6 text-muted-foreground">Loading…</td></tr>
                                )}
                                {!loading && visits.length === 0 && (
                                    <tr><td colSpan={10} className="text-center p-8 text-muted-foreground">No visits found.</td></tr>
                                )}
                                {visits.map((v) => (
                                    <tr key={v.id} data-testid={`row-visit-${v.id}`} className="hover:bg-accent/40">
                                        <td className="px-4 py-3">
                                            <div className="font-semibold">{v.visitor_name}</div>
                                            <div className="text-xs text-muted-foreground">{v.company || "—"}</div>
                                        </td>
                                        <Td>{v.visitor_phone}</Td>
                                        <Td>{v.host_name || "—"}</Td>
                                        <Td>{v.gate_name || "—"}</Td>
                                        <Td><span className="text-[12px]">{v.visit_type || "—"}</span></Td>
                                        <Td><span className="text-[12px] line-clamp-2 max-w-[220px]" title={v.purpose || ""}>{v.purpose || "—"}</span></Td>
                                        <Td>{fmtDate(v.check_in_at)}</Td>
                                        <Td>{fmtDate(v.check_out_at)}</Td>
                                        <Td><StatusBadge status={v.status} /></Td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <div className="flex justify-end gap-1.5">
                                                {canCorrect && (
                                                    <Button size="sm" variant="outline" className="h-8"
                                                        title="Correct host / visit type / purpose"
                                                        onClick={() => setEditVisit(v)}
                                                        data-testid={`btn-correct-${v.id}`}>
                                                        <PencilSimple size={14} />
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="outline" className="h-8"
                                                    onClick={() => nav(`/checkpoint/pass/${v.id}`)}
                                                    data-testid={`btn-view-pass-${v.id}`}>
                                                    <IdentificationCard size={14} />
                                                </Button>
                                                {v.status === "pending" && (
                                                    <>
                                                        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                                                            onClick={() => setStatus(v.id, "approved")}
                                                            data-testid={`btn-approve-${v.id}`}>
                                                            <CheckCircle size={14} weight="bold" />
                                                        </Button>
                                                        <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700 text-white"
                                                            onClick={() => setStatus(v.id, "rejected")}
                                                            data-testid={`btn-reject-${v.id}`}>
                                                            <XCircle size={14} weight="bold" />
                                                        </Button>
                                                    </>
                                                )}
                                                {!v.check_out_at && v.status !== "rejected" && (
                                                    <Button size="sm" className="h-8 bg-brand hover:bg-brand/90 text-white"
                                                        onClick={() => checkOut(v.id)}
                                                        data-testid={`btn-checkout-${v.id}`}>
                                                        <SignOut size={14} weight="bold" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </div>
    );
}

function Th({ children, align }) {
    return <th className={`px-4 py-3 text-xs uppercase tracking-widest font-semibold text-muted-foreground text-${align || "left"}`}>{children}</th>;
}
function Td({ children }) { return <td className="px-4 py-3">{children}</td>; }
function fmtDate(iso) {
    return fmtIST(iso, { date: true, time: true });
}

// ─────────── Correct visit details (capability: correct_visit_details) ───────────
function EditVisitDialog({ visit, onClose, onSaved }) {
    const [userDirectory, setUserDirectory] = useState([]);
    const [hosts, setHosts] = useState([]);
    const [visitTypes, setVisitTypes] = useState([]);
    const [hostOpen, setHostOpen] = useState(false);
    const [hostRef, setHostRef] = useState(""); // "user:{id}" | "host:{id}" | ""
    const [visitType, setVisitType] = useState("");
    const [purpose, setPurpose] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!visit) return;
        setHostRef(visit.host_user_id ? `user:${visit.host_user_id}` : visit.host_id ? `host:${visit.host_id}` : "");
        setVisitType(visit.visit_type || "");
        setPurpose(visit.purpose || "");
        (async () => {
            try {
                const [dir, h, vt] = await Promise.all([
                    api.get("/gate-check/hosts/directory"),
                    api.get("/gate-check/hosts"),
                    api.get("/gate-check/lookups/visit-types"),
                ]);
                setUserDirectory(dir.data);
                setHosts(h.data);
                setVisitTypes(vt.data?.values || []);
            } catch { /* silent */ }
        })();
    }, [visit]);

    if (!visit) return null;

    const selectedUserHost = userDirectory.find((u) => `user:${u.id}` === hostRef);
    const selectedDirHost = hosts.find((h) => `host:${h.id}` === hostRef);
    const hostLabel = selectedUserHost?.name || selectedDirHost?.name || "No host";

    const save = async () => {
        setBusy(true);
        try {
            const payload = { visit_type: visitType, purpose };
            if (hostRef.startsWith("user:")) payload.host_user_id = hostRef.slice(5);
            else if (hostRef.startsWith("host:")) payload.host_id = hostRef.slice(5);
            else payload.clear_host = true;
            const { data } = await api.patch(`/gate-check/visits/${visit.id}/details`, payload);
            toast.success(data.changed ? "Visit corrected (audited)" : "No changes");
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={!!visit} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="edit-visit-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PencilSimple className="text-brand" size={18} /> Correct Visit — {visit.visitor_name}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="text-[12px] text-muted-foreground">
                        Corrections are audited with old → new values. Works on any visit status, including checked-out.
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-wider text-slate-600">Host / Employee</Label>
                        <Popover open={hostOpen} onOpenChange={setHostOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="mt-1 w-full h-11 px-3 rounded-md border border-input bg-white flex items-center justify-between text-sm hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand"
                                    data-testid="edit-host"
                                >
                                    <span className={hostRef ? "text-slate-800 truncate" : "text-muted-foreground"}>{hostLabel}</span>
                                    <CaretUpDown size={14} className="text-slate-400 shrink-0" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                                <Command>
                                    <CommandInput placeholder="Search host by name…" data-testid="edit-host-search" />
                                    <CommandList className="max-h-[240px] overflow-y-auto overscroll-contain touch-pan-y"
                                        onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
                                        <CommandEmpty>No matching host</CommandEmpty>
                                        <CommandGroup>
                                            <CommandItem value="__none__" onSelect={() => { setHostRef(""); setHostOpen(false); }} data-testid="edit-host-none">
                                                <span className="text-slate-400 italic">No host</span>
                                            </CommandItem>
                                        </CommandGroup>
                                        <CommandGroup heading="Ops IQ Users">
                                            {userDirectory.map((u) => (
                                                <CommandItem key={u.id} value={u.name}
                                                    onSelect={() => { setHostRef(`user:${u.id}`); setHostOpen(false); }}
                                                    data-testid={`edit-host-user-${u.id}`}>
                                                    <div className="flex-1 min-w-0 text-[13px] font-medium text-slate-800 truncate">{u.name}</div>
                                                    {hostRef === `user:${u.id}` && <Check size={14} className="text-brand shrink-0 ml-2" />}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                        {hosts.length > 0 && (
                                            <CommandGroup heading="External Hosts">
                                                {hosts.map((h) => (
                                                    <CommandItem key={h.id} value={h.name}
                                                        onSelect={() => { setHostRef(`host:${h.id}`); setHostOpen(false); }}
                                                        data-testid={`edit-host-ext-${h.id}`}>
                                                        <div className="flex-1 min-w-0 text-[13px] font-medium text-slate-800 truncate">{h.name}</div>
                                                        {hostRef === `host:${h.id}` && <Check size={14} className="text-brand shrink-0 ml-2" />}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        )}
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-wider text-slate-600">Visit Type *</Label>
                        <Select value={visitType} onValueChange={setVisitType}>
                            <SelectTrigger className="mt-1 h-11" data-testid="edit-visit-type"><SelectValue placeholder="Select visit type…" /></SelectTrigger>
                            <SelectContent>
                                {visitTypes.map((v) => (
                                    <SelectItem key={v} value={v} data-testid={`edit-vt-${v}`}>{v}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-wider text-slate-600">Purpose of Visit *</Label>
                        <Textarea rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)} className="mt-1" data-testid="edit-purpose" />
                    </div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button type="button" onClick={save} disabled={busy || !visitType || !purpose.trim()} className="bg-brand hover:bg-brand/90 text-white" data-testid="edit-visit-save">
                        {busy ? "Saving…" : "Save corrections"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
